import React, { useContext, useEffect, useRef, useState } from "react";
import {
  notifyError,
  roundToThreeDecimal,
} from "../../../../../helpers/helpers";
import { useMediaQuery } from "react-responsive";
import { useDispatch, useSelector } from "react-redux";
import { postLiveRoomData } from "../../../../../redux/action/liveRoom";
import ConnectionStatus from "./ConnectionStatus";
import { ClipLoader } from "react-spinners";
import { getSocket } from "../../../../../helpers/SingletonSocketService";
import { globalContext } from "../../../../../context/ContextProvider";
import MinAndHighestPrice from "./MinAndHighestPrice";
import { Modal } from "react-materialize";
import PayemdInLiveRoom from "./PayemdInLiveRoom";
import { useParams } from "react-router-dom";

function LiveBidingArea({
  liveOfferData,
  timeEnded,
  startTimer,
  setBidderDetails,
  index,
}) {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isDesktop = useMediaQuery({ query: "(min-width: 1600px)" });
  const isLaptop = useMediaQuery({ query: "(max-width: 1200px)" });
  const isSmallTablet = useMediaQuery({ query: "(max-width: 750px)" });
  const [bidAmount, setBidAmount] = useState("");
  const [bidPriceUpdateValue, setBidPriceUpdateValue] = useState({});
  const [myCurrentBid, setMyCurrentBid] = useState(0);
  const { profile } = useSelector((state) => state?.profileStore);
  const { loading, loadingId } = useSelector((state) => state.liveAuctionList);
  const { connectingWithLiveRoom } = useContext(globalContext);

  //socket connection for live room
  useEffect(() => {
    const socket = getSocket();
    socket?.on("newBid", (data) => {
      if (data?.offerId === liveOfferData?._id) {
        const { bidHistory, ...restData } = data;
        setBidPriceUpdateValue(restData);
        setBidderDetails([
          {
            myBidColor:
              data?.bidDetails?.organization === profile?.organization,
            bidHistory,
          },
        ]);
      }
    });

    document.body.style.overflow = "auto";
  }, [liveOfferData, profile, setBidderDetails]);


  //handle submit bid functionality for live room
  const handleBidUpdateValue = async (e) => {
    e.preventDefault();
    const FirstBidTime = bidPriceUpdateValue?.highestBid === 0 ? liveOfferData?.startingPrice : bidPriceUpdateValue?.highestBid;
    const value = Number(bidAmount);
    const {  minimumBid } = liveOfferData;
    const difference = +(value - FirstBidTime).toFixed(10);
    const multiplied = +(difference / minimumBid).toFixed(10);
    const nearestInteger = Math.round(multiplied);
    const remainder = Math.abs(multiplied - nearestInteger);
    const tolerance = 0.0001;

    if (timeEnded) {
      return notifyError("Time has ended");
    } else if (startTimer) {
      return notifyError("Auction for this lot has not started yet");
    } else if (
      bidPriceUpdateValue?.highestBid !== 0 &&
      value <
        +(
          (liveOfferData?.minimumBid || 0) + bidPriceUpdateValue?.highestBid
        ).toFixed(10)
    ) {
      return notifyError(
        `Bid amount should be greater than highest bid plus minimum bid amount. Minimum amount is ${+(
          (liveOfferData?.minimumBid || 0) +
          (bidPriceUpdateValue?.highestBid || 0)
        ).toFixed(10)}`
      );
    } else if (
      value <
        +(
          (liveOfferData?.minimumBid || 0) + liveOfferData?.startingPrice
        ).toFixed(10)
    ) {
      return notifyError(
        `Bid amount should be greater than Starting price plus minimum bid amount. Minimum amount is ${+(
          (liveOfferData?.minimumBid || 0) + liveOfferData?.startingPrice
        ).toFixed(10)}`
      );
    } else if (Math.abs(remainder) > tolerance) {
      const { minimumBid } = liveOfferData;
      const nearestMultiple = Math.ceil(difference / minimumBid);
      const nearestValidBid = FirstBidTime + nearestMultiple * minimumBid;
      const previousValidBid = nearestValidBid - minimumBid;

      const isDecimal = !Number.isInteger(minimumBid);
      const format = (num) => (isDecimal ? num.toFixed(2) : num.toFixed(0));

      return notifyError(
        `Bid amount should be in multiples of ₹${format(minimumBid)}. ` +
          `Nearest valid bid is ₹${format(previousValidBid)} or ₹${format(
            nearestValidBid
          )}`
      );
    } else {
      dispatch(postLiveRoomData(value, true, id, liveOfferData?._id)).then(
        () => {
          inputRef.current?.blur();
        }
      );
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  //find the current bid of the user after refesh the page
  useEffect(() => {
    if (liveOfferData) {
      setBidPriceUpdateValue({
        highestBid: liveOfferData?.highestBid,
        offerId: liveOfferData?._id,
        myHighestBid: liveOfferData?.myCurrentBidAmount,
        bidDetails: {
          organization: profile?.organization,
          bidAmount: liveOfferData?.myCurrentBidAmount,
          autoBidAmount: null,
        },
      });
      setBidderDetails([
        { myBidColor: true, bidHistory: liveOfferData?.bidHistory },
      ]);
    }
  }, [liveOfferData, profile, setBidderDetails]);

  //find the current bid of the user
  useEffect(() => {
    if (
      profile?.organization === bidPriceUpdateValue?.bidDetails?.organization
    ) {
      setMyCurrentBid(bidPriceUpdateValue?.myHighestBid);
    }
  }, [bidPriceUpdateValue, profile]);

  useEffect(() => {
    if (bidPriceUpdateValue?.highestBid === 0) {
      setBidAmount(liveOfferData?.startingPrice);
    } else {
      setBidAmount(bidPriceUpdateValue?.highestBid);
    }
  }, [bidPriceUpdateValue, liveOfferData, profile, myCurrentBid]);

  if (connectingWithLiveRoom) {
    return <ConnectionStatus />;
  }

  return (
    <div
      className={`${
        isSmallTablet
          ? "valign-wrapper gap-2 column justify-center"
          : isLaptop && "valign-wrapper justify-center flex-wrap gap-3"
      }  `}
      style={{
        display: !isLaptop && "grid",
        gridTemplateColumns: "400px 1fr 1fr",
        padding: "1rem",
      }}
    >
      <MinAndHighestPrice
        liveOfferData={liveOfferData}
        bidPriceUpdateValue={bidPriceUpdateValue}
        timeEnded={timeEnded}
        startTimer={startTimer}
        index={index}
      />

      {/* {!liveOfferData?.isPaid ? (
        <div className="valign-wrapper justify-center"
          style={{
            opacity: timeEnded && 0.3,
            order: 3,
          }}
        >
          <button
            className={`pointergrey lighten-2 grey-text white-text cercle-purple font-18px`}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              width: "200px",
            }}
            disabled={timeEnded}
            onClick={() => setIsModalOpen(true)}
          >
           Pay EMD Now
          </button>
        </div>
      ) : ( */}
      <div
        className="flex column align-center gap-10px"
        style={{
          opacity: timeEnded && 0.3,
          order: 3,
        }}
      >
        <h5 className={`margin-0px font-18px`}>Enter your bid</h5>
        <form
          onSubmit={(e) => handleBidUpdateValue(e)}
          className="full-width column flex align-center gap-1 justify-center"
        >
          <div className="flex gap-1">
            <button
              type="button"
              onKeyDown={handleKeyDown}
              className="circle valign-wrapper justify-center pointer"
              style={{
                width: "45px",
                height: "45px",
                backgroundImage:
                  bidPriceUpdateValue?.highestBid === bidAmount
                    ? "linear-gradient(rgb(150, 150, 150),rgb(230, 225, 235))"
                    : "linear-gradient(#4C2171, #6F2DA8)",
              }}
              disabled={timeEnded}
              onClick={() =>
                setBidAmount((prev) => {
                  const current = Number(prev) || 0;
                  const decrement = liveOfferData?.minimumBid || 0;
                  const highestBid = bidPriceUpdateValue?.highestBid || 0;
                  const updated = +(current - decrement).toFixed(10);
                  return Math.max(highestBid, updated);
                })
              }
            >
              <span
                className="white-text"
                style={{ fontSize: "45px", fontWeight: 600 }}
              >
                -
              </span>
            </button>
            <span className="border-radius-12 flex column valign-wrapper justify-center">
              <input
                ref={inputRef}
                value={bidAmount}
                type="number"
                step="0.01"
                min="0"
                disabled={timeEnded}
                className="browser-default white font-25px"
                onChange={(e) => setBidAmount(e.target.value)}
                style={{
                  width: isDesktop ? "230px" : "200px",
                  boxShadow:
                    "-4px -5px 10px 0 white inset, 3px 3px 8px 0 rgba(0, 0, 0, 0.25) inset",
                  height: "50px",
                  fontWeight: "600",
                  borderRadius: 10,
                  padding: "12px 12px 12px 15px",
                  textAlign: "center",
                }}
              />

              <span className={`current-bid-amount`}>
                <p className={`small-text`}>
                  Your Bid Amount: {roundToThreeDecimal(myCurrentBid) || 0}
                </p>
              </span>
            </span>
            <button
              type="button"
              className="circle valign-wrapper justify-center pointer circle"
              style={{
                width: "45px",
                height: "45px",
                backgroundImage: "linear-gradient(#4C2171, #6F2DA8)",
              }}
              disabled={timeEnded}
              onKeyDown={handleKeyDown}
              onClick={() =>
                setBidAmount((prev) => {
                  const current = Number(prev) || 0;
                  const increment = liveOfferData?.minimumBid || 0;
                  const updated = +(current + increment).toFixed(10);
                  return updated;
                })
              }
            >
              <span
                className="white-text"
                style={{ fontSize: "45px", fontWeight: 600 }}
              >
                +
              </span>
            </button>
          </div>
          <button
            className={`pointergrey lighten-2 grey-text white-text cercle-purple font-20px`}
            type="submit"
            style={{
              padding: "10px 40px",
              borderRadius: "8px",
              width: "150px",
              height: "45px",
            }}
            disabled={
              timeEnded || (loading && loadingId === liveOfferData?._id)
            }
          >
            {loading && loadingId === liveOfferData?._id ? (
              <ClipLoader color="#fff" size={20} />
            ) : (
              "Submit"
            )}
          </button>
        </form>
      </div>
      {/* )} */}

      <Modal
        actions={[]}
        open={isModalOpen}
        options={{ onCloseEnd: closeModal }}
        className="modelAccount"
      >
        <PayemdInLiveRoom
          liveOfferData={liveOfferData}
          setIsModalOpen={setIsModalOpen}
        />
      </Modal>
    </div>
  );
}

export default LiveBidingArea;
